import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, Typography } from "@mui/material";
import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";

export const ACTION_BUTTON_FIELD_TYPE = "actionButton" as const;

// Marks the field's grid cell so the form route can promote it to position: sticky.
export const APP_STICKY_HEADER_CLASS = "vmd-app-sticky-header";

export type ActionButtonFieldDefinition = BaseFormFieldDefinition<
  typeof ACTION_BUTTON_FIELD_TYPE
> & {
  buttonLabel?: string;
  buttonVariant?: "outlined" | "contained" | "text";
  buttonColor?: "primary" | "error";
  buttonIcon?: "add" | "delete";
  buttonAlign?: "left" | "right";
  // When set, renders a sticky header row with this title on the left and the button(s) on the right.
  headerTitle?: string;
  onButtonClick?: () => void;
  // Optional second button rendered next to the primary one (e.g. Save alongside Delete).
  secondaryButtonLabel?: string;
  secondaryButtonColor?: "primary" | "error";
  secondaryButtonIcon?: "add" | "delete";
  secondaryButtonDisabled?: boolean;
  onSecondaryButtonClick?: () => void;
};

export const ActionButtonFormField = (
  props: ActionButtonFieldDefinition & AdditionalFormFieldProps
) => {
  const {
    buttonLabel,
    buttonVariant = "outlined",
    buttonColor = "primary",
    buttonIcon,
    buttonAlign = "left",
    headerTitle,
    onButtonClick,
    disabled,
    secondaryButtonLabel,
    secondaryButtonColor = "primary",
    secondaryButtonIcon,
    secondaryButtonDisabled,
    onSecondaryButtonClick,
  } = props;

  const iconFor = (which?: "add" | "delete") =>
    which === "delete" ? (
      <DeleteOutlineIcon fontSize="small" />
    ) : which === "add" ? (
      <AddIcon fontSize="small" />
    ) : undefined;

  const buttonSx = {
    textTransform: "none",
    fontSize: "14px",
    borderRadius: "4px",
    padding: "7px 16px",
    whiteSpace: "nowrap",
  } as const;

  const button = (
    <Button
      variant={buttonVariant}
      color={buttonColor}
      startIcon={iconFor(buttonIcon)}
      disabled={disabled}
      onClick={() => onButtonClick?.()}
      sx={buttonSx}
    >
      {buttonLabel ?? "Button"}
    </Button>
  );

  const secondaryButton =
    secondaryButtonLabel !== undefined ? (
      <Button
        variant={buttonVariant}
        color={secondaryButtonColor}
        startIcon={iconFor(secondaryButtonIcon)}
        disabled={secondaryButtonDisabled}
        onClick={() => onSecondaryButtonClick?.()}
        sx={buttonSx}
      >
        {secondaryButtonLabel}
      </Button>
    ) : null;

  if (headerTitle !== undefined) {
    return (
      <Box
        className={APP_STICKY_HEADER_CLASS}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1,
        }}
      >
        <Typography variant="h5" component="h2">
          {headerTitle}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {button}
          {secondaryButton}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: buttonAlign === "right" ? "flex-end" : "flex-start",
      }}
    >
      {button}
    </Box>
  );
};
